import { useState } from "react";

/**
 * Open state for a popup that is controlled by `open` when given, and that
 * calls `onSeed` on every open, whether a click or `open` opened it, so its
 * draft starts from the current value. `initialSeededOpen` is the open state
 * the mount draft already covers: pass the mount's open state when the draft
 * starts from the value, or `false` so an open mount seeds too.
 * `setOpen` updates the internal state and reports to `onOpenChange`.
 */
export const useSeededOpen = ({
    open,
    onOpenChange,
    initialSeededOpen,
    onSeed,
}: {
    open: boolean | undefined;
    onOpenChange: ((isOpen: boolean) => void) | undefined;
    initialSeededOpen: boolean;
    onSeed: () => void;
}) => {
    const [isInternallyOpen, setIsInternallyOpen] = useState(false);
    const isOpen = open ?? isInternallyOpen;
    const [wasSeededOpen, setWasSeededOpen] = useState(initialSeededOpen);
    if (isOpen !== wasSeededOpen) {
        setWasSeededOpen(isOpen);
        if (isOpen) {
            onSeed();
        }
    }
    const setOpen = (nextIsOpen: boolean) => {
        setIsInternallyOpen(nextIsOpen);
        onOpenChange?.(nextIsOpen);
    };
    return { isOpen, setOpen };
};
